import { useRef, useEffect, forwardRef } from 'react';
import type {
  ReactNode,
  PropsWithoutRef,
  Ref,
  MouseEventHandler,
  MouseEvent as ReactMouseEvent
} from 'react';
import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { meetsContrastGuidelines, mix } from 'polished';

import type {
  BaseProps,
  ForwardProps,
  ForwardRefForwardPropsComponent,
  PropsWithDefaults,
  AsProp
} from '../../types';
import { defaultThemeProp } from '../../theme';
import { isPositionWithinRect, tryCatch, createClassName } from '../../utils';
import {
  getActiveColors,
  getHoverColors,
  isSolidColor,
  omitProps,
  readableColor,
  readableHue
} from '../../styles';
import { useElement, useConsolidatedRef } from '../../hooks';
import Tooltip from '../Tooltip';
import Progress, { StyledProgressRing } from '../Progress';
import { StyledBackdrop } from '../Backdrop';
import { StyledIcon } from '../Icon';
import { StyledPopover } from '../Popover';

export interface ButtonProps extends BaseProps, AsProp {
  /** Text or content for the Button. */
  children: ReactNode;
  /**
   * Determines if the Button will be disabled.
   * @default false
   */
  disabled?: boolean;
  /** A location to navigate to. Passing an href will render an anchor styled as a Button. */
  href?: string;
  /**
   * Controls the styling of the Button.
   * @default 'secondary'
   */
  variant?: 'primary' | 'secondary' | 'simple' | 'link' | 'text';
  /**
   * Controls the behavior of a Button within a Form.
   * @default 'button'
   */
  type?: 'button' | 'reset' | 'submit';
  /**
   * Set the Icon prop to `true` if you're using just an Icon in your Button. Make sure to pass an Icon as children if `true`.
   * @default false
   */
  icon?: boolean;
  /**
   * Used for a smaller sized button.
   * @default false
   */
  compact?: boolean;
  /** The label text will be used inside a Tooltip. */
  label?: string;
  /** The aria label text will be inserted as the aria-label on the Button. */
  'aria-label'?: string;
  /**
   * Applies visual disabled styles without disabling the button element itself.
   * @default false
   */
  'aria-disabled'?: boolean | 'true' | 'false';
  /**
   * Optionally renders an indeterminate progress indicator in a button.
   * @default false
   */
  loading?: boolean;
  /** Ref forwarded to the wrapping element. */
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement>;
}

type ButtonPropsWithDefaults = PropsWithDefaults<
  ButtonProps,
  'variant' | 'type' | 'disabled' | 'icon' | 'loading'
>;

const baseStyles = (theme: DefaultTheme) => {
  const {
    base: {
      spacing,
      animation: {
        speed,
        timing: { ease }
      },
      'disabled-opacity': disabledOpacity
    },
    components: {
      button: { 'focus-shadow': focusShadow }
    }
  } = theme;

  return css`
    text-decoration: none;
    transition-property: background-color, color, border-color, box-shadow, translate;
    transition-duration: calc(0.5 * ${speed});
    transition-timing-function: ${ease};
    cursor: pointer;

    @media (forced-colors: none) {
      outline: none;
    }

    & + & {
      margin-inline-start: ${spacing};
    }

    /* Not able to combine with selector above. Stylis bug? */

    & + ${StyledPopover} + & {
      margin-inline-start: ${spacing};
    }

    &:disabled,
    &[disabled] {
      opacity: ${disabledOpacity};
      cursor: not-allowed;
      pointer-events: none;
    }

    &[aria-disabled='true'] {
      opacity: ${disabledOpacity};
    }

    &:not([disabled], [aria-disabled='true']):focus,
    &:enabled:not([aria-disabled='true']):focus {
      box-shadow: ${focusShadow};
    }
  `;
};

/* If the background color is not provided, the color will be used for the hover and active styles */
const getFilledButtonStyles = (color: string, backgroundColor?: string) => ({
  contrastColor: backgroundColor ? color : tryCatch(() => readableColor(color)),
  hoverColors: getHoverColors(backgroundColor || color),
  activeColors: getActiveColors(backgroundColor || color)
});

const getOutlinedButtonStyles = (color: string, backgroundColor: string) => {
  // If the background color is not solid, use the readable color for the text
  const fallbackBackground = isSolidColor(backgroundColor) ? backgroundColor : readableColor(color);
  const backgroundUsable = tryCatch(() => meetsContrastGuidelines(color, fallbackBackground).AA);
  const usableColor = backgroundUsable ? color : readableHue(color, backgroundColor);
  const hoverBackgroundColor = tryCatch(() => mix(0.9, backgroundColor, color));
  const activeBackgroundColor = tryCatch(() => mix(0.8, backgroundColor, usableColor));

  return {
    textColor: backgroundUsable ? color : readableColor(color),
    hoverBg: hoverBackgroundColor,
    hoverFg: readableHue(color, hoverBackgroundColor ?? 'transparent'),
    activeBg: tryCatch(() => mix(0.8, backgroundColor, usableColor)),
    activeFg: readableHue(usableColor, activeBackgroundColor ?? 'transparent')
  };
};

export const StyledButton = styled.button.withConfig(omitProps('loading'))<ButtonPropsWithDefaults>(
  ({ variant, icon, loading, compact, theme }) => {
    const {
      base: {
        spacing,
        'border-radius': baseBorderRadius,
        palette: { 'primary-background': backgroundColor },
        'hit-area': {
          'mouse-min': hitAreaMouse,
          'finger-min': hitAreaFinger,
          'compact-min': hitAreaCompact
        },
        animation: { speed }
      },
      components: {
        button: {
          color,
          'foreground-color': foregroundColor,
          'secondary-color': secondaryColor,
          'secondary-fill-style': fillStyle,
          padding,
          'border-radius': borderRadius,
          'border-width': borderWidth,
          touch: { padding: touchPadding }
        }
      }
    } = theme;

    // Base
    const base = baseStyles(theme);

    // Non-text base
    const nonTextual = css`
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: ${hitAreaMouse};
      min-width: ${hitAreaMouse};
      border: ${borderWidth} solid transparent;
      border-radius: calc(${baseBorderRadius} * ${borderRadius});
      -webkit-user-select: none;
      user-select: none;
      white-space: nowrap;

      ${!icon &&
      css`
        padding: ${padding};
      `}

      ${icon &&
      css`
        border-radius: calc(${baseBorderRadius} * ${borderRadius});

        > svg {
          display: block;
        }
      `} ${compact &&
      css`
        min-height: ${hitAreaCompact};
        min-width: ${hitAreaCompact};
      `} @media (
        pointer: coarse) {
        border-radius: calc(${baseBorderRadius} * ${borderRadius});

        ${!compact &&
        css`
          min-height: ${hitAreaFinger};
          min-width: ${hitAreaFinger};
        `}

        ${!icon &&
        css`
          padding: ${touchPadding};
        `}
      }

      &:active {
        translate: 0 0.0625rem;
      }
    `;

    // Primary and Secondary-filled
    /* For primary button, use the foreground color as the text color. If foreground is 'auto', then calculate based on background
       For secondary button, always use the secondary color */
    const { contrastColor, hoverColors, activeColors } =
      variant !== 'primary' || foregroundColor === 'auto'
        ? getFilledButtonStyles(variant === 'primary' ? color : secondaryColor)
        : getFilledButtonStyles(foregroundColor, color);

    const solidButtonStyles = css`
      --button-background-color: ${variant === 'primary' ? color : secondaryColor};
      color: ${contrastColor};
      background-color: ${variant === 'primary' ? color : secondaryColor};

      @media (hover: hover) {
        &:hover {
          background-color: ${hoverColors.background};
          text-decoration: none;
        }
      }

      &:active {
        background-color: ${activeColors.background};
        color: ${activeColors.foreground};
        text-decoration: none;
      }
    `;

    // Secondary-outline
    const secondaryOutlineButtonStyles = getOutlinedButtonStyles(secondaryColor, backgroundColor);
    const secondary = css`
      --button-background-color: ${backgroundColor};
      color: ${secondaryOutlineButtonStyles.textColor};
      background-color: ${backgroundColor};
      border-color: ${secondaryColor};

      @media (hover: hover) {
        &:hover {
          background-color: ${secondaryOutlineButtonStyles.hoverBg};
          text-decoration: none;
        }
      }

      &:active {
        color: ${secondaryOutlineButtonStyles.activeFg};
        background-color: ${secondaryOutlineButtonStyles.activeBg};
        border-color: ${secondaryOutlineButtonStyles.activeFg};
        text-decoration: none;
      }
    `;

    // Simple
    const simpleButtonStyles = getOutlinedButtonStyles(
      fillStyle === 'outline' ? secondaryColor : color,
      backgroundColor
    );
    const simple = css`
      --button-background-color: ${icon ? 'transparent' : backgroundColor};
      color: ${icon ? 'currentColor' : simpleButtonStyles.textColor};
      background-color: ${icon ? 'transparent' : backgroundColor};

      @media (hover: hover) {
        &:hover {
          ${icon &&
          css`
            &::before {
              content: '';
              position: absolute;
              top: calc(${borderWidth} * -1);
              bottom: calc(${borderWidth} * -1);
              left: calc(${borderWidth} * -1);
              right: calc(${borderWidth} * -1);
              border-radius: inherit;
              background-color: currentColor;
              opacity: 0.1;
            }
          `}

          ${!icon &&
          css`
            background-color: ${simpleButtonStyles.hoverBg};
            color: ${simpleButtonStyles.hoverFg};
            border-color: ${simpleButtonStyles.hoverFg};
            text-decoration: none;
          `}
        }

        &:active {
          ${icon &&
          css`
            &::before {
              content: '';
              position: absolute;
              top: calc(${borderWidth} * -1);
              bottom: calc(${borderWidth} * -1);
              left: calc(${borderWidth} * -1);
              right: calc(${borderWidth} * -1);
              border-radius: inherit;
              background-color: currentColor;
              opacity: 0.2;
            }
          `}

          ${!icon &&
          css`
            background-color: ${simpleButtonStyles.activeBg};
            color: ${simpleButtonStyles.activeFg};
            border-color: ${simpleButtonStyles.activeFg};
            text-decoration: none;
          `}
        }
      }
    `;

    return css`
      ${base}
      ${nonTextual}
        ${(variant === 'primary' || (variant === 'secondary' && fillStyle === 'fill')) &&
      solidButtonStyles}
        ${variant === 'secondary' && fillStyle === 'outline' && secondary}
        ${variant === 'simple' && simple}
        ${variant === 'primary' &&
      css`
        font-weight: ${theme.base['font-weight']['semi-bold']};
      `}

        ${loading &&
      css`
        ${StyledBackdrop} {
          background-color: var(--button-background-color);
          border-radius: inherit;
        }

        ${StyledProgressRing} {
          width: 1em;
          height: 1em;

          ${!icon &&
          css`
            margin-inline-end: calc(${spacing} / 2);
          `}
          circle:nth-child(2) {
            animation-duration: calc(${speed} * 2);
          }
        }
      `}
    `;
  }
);

StyledButton.defaultProps = defaultThemeProp;

export const StyledButtonLink = styled.a.withConfig(omitProps('loading'))<
  Pick<ButtonPropsWithDefaults, 'variant' | 'href'> &
    // Needed for omitProps
    Pick<ButtonProps, 'loading'>
>(({ theme, variant, href }) => {
  const {
    base: {
      palette: { 'foreground-color': textVariantColor }
    },
    components: {
      button: { color: primaryColor, 'secondary-fill-style': fillStyle },
      link: { color: linkColor }
    }
  } = theme;

  let linkVariantColor = linkColor;
  if (!href && fillStyle === 'solid') {
    linkVariantColor = primaryColor;
  }
  const { background: activeLinkColor } = getActiveColors(linkVariantColor);
  const { background: activeTextColor } = getActiveColors(textVariantColor);

  const base = baseStyles(theme);

  const textual = css`
    background-color: transparent;
    display: inline;
    text-align: start;
    border: none;

    @media (hover: hover) {
      &:hover {
        text-decoration: underline;
      }
    }

    > ${StyledIcon} {
      vertical-align: bottom;
    }
  `;

  const link = css`
    color: ${linkVariantColor};

    &:active {
      color: ${activeLinkColor};
    }
  `;

  const text = css`
    color: ${textVariantColor};

    &:active {
      color: ${activeTextColor};
    }
  `;

  return css`
    ${base}
    ${textual}
      ${variant === 'link' && link}
      ${variant === 'text' && text}
  `;
});

StyledButtonLink.defaultProps = defaultThemeProp;

const Button: ForwardRefForwardPropsComponent<ButtonProps> = forwardRef(function Button(
  {
    variant = 'secondary',
    type = 'button',
    disabled = false,
    icon = false,
    compact = false,
    href,
    as,
    forwardedAs,
    label,
    'aria-label': ariaLabel,
    'aria-disabled': ariaDisabled,
    loading = false,
    children,
    className,
    ...restProps
  }: PropsWithoutRef<ButtonProps> & ForwardProps,
  ref: ButtonProps['ref']
) {
  const [buttonEl, setButtonEl] = useElement<HTMLButtonElement | HTMLAnchorElement>();
  const mouseDownEvent = useRef<ReactMouseEvent>();
  const mouseDownPosition = useRef<DOMRect>();
  const mouseDownTimestamp = useRef<number>();
  const buttonRef = useConsolidatedRef(ref, setButtonEl);
  const isTextual = ['link', 'text'].includes(variant);
  const showProgress = loading && !isTextual;
  const StyledComp: React.ElementType = isTextual ? StyledButtonLink : StyledButton;

  const handleClickWithElementShift = (e: MouseEvent) => {
    if (
      buttonRef.current &&
      mouseDownPosition.current &&
      mouseDownTimestamp.current &&
      isPositionWithinRect({ x: e.clientX, y: e.clientY }, mouseDownPosition.current) &&
      Date.now() - mouseDownTimestamp.current < 500
    ) {
      // Only click if the button is still the topmost element at its own position.
      // If something has rendered over the button between mousedown and mouseup,
      // elementFromPoint will return that — not the button — and we should not trigger a click.
      const { x, y, width, height } = buttonRef.current.getBoundingClientRect();
      const elementAtPoint = document.elementFromPoint(x + width / 2, y + height / 2);
      if (buttonRef.current.contains(elementAtPoint) || elementAtPoint === buttonRef.current) {
        buttonRef.current.click();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleClickWithElementShift);

    return () => {
      document.removeEventListener('mouseup', handleClickWithElementShift);
      mouseDownEvent.current = undefined;
      mouseDownPosition.current = undefined;
      mouseDownTimestamp.current = undefined;
    };
  }, []);

  return (
    <>
      <StyledComp
        {...restProps}
        ref={buttonRef}
        as={as || (href ? 'a' : 'button')}
        forwardedAs={forwardedAs || (href ? 'a' : 'button')}
        className={createClassName('button', className, { variant, icon, compact, loading })}
        variant={variant}
        icon={icon}
        compact={compact}
        type={href ? undefined : type}
        href={href}
        disabled={disabled && !href}
        aria-disabled={ariaDisabled}
        loading={showProgress}
        aria-label={ariaLabel || label}
        onMouseDown={(e: ReactMouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
          (restProps.onMouseDown as MouseEventHandler | undefined)?.(e);
          e.persist();
          mouseDownEvent.current = e;
          mouseDownPosition.current = e.currentTarget.getBoundingClientRect();
          mouseDownTimestamp.current = Date.now();
        }}
        onMouseUp={(e: ReactMouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
          const shouldFocus = mouseDownEvent.current && !mouseDownEvent.current.defaultPrevented;
          mouseDownEvent.current = undefined;
          mouseDownPosition.current = undefined;
          mouseDownTimestamp.current = undefined;

          if (shouldFocus && buttonRef.current !== document.activeElement) {
            buttonRef.current?.focus();
          }

          return (restProps.onMouseUp as MouseEventHandler | undefined)?.(e);
        }}
      >
        {showProgress && <Progress variant='ring' placement='inline' />}
        {!icon || !showProgress ? children : null}
      </StyledComp>

      {buttonEl && label && (
        <Tooltip target={buttonEl} showDelay='none' hideDelay='none' describeTarget={false}>
          {label}
        </Tooltip>
      )}
    </>
  );
});

export default Button;
